<template>
  <div class="video-demo">
    <!-- Video Conference Interface -->
    <div class="bg-white rounded-lg border border-gray-200">
      <!-- Header -->
      <div class="bg-purple-600 text-white p-3 rounded-t-lg flex items-center justify-between">
        <div class="flex items-center gap-2">
          <IconifyIcon icon="heroicons:video-camera-20-solid" class="w-5 h-5" />
          <span class="font-medium">Video Call Demo</span> <span class="ml-1 text-xs font-normal opacity-80">(simulated, no real connection)</span>
        </div>
        <div class="flex items-center gap-1">
          <div class="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
          <span class="text-xs">{{ callDuration }}</span>
        </div>
      </div>

      <!-- Video Area -->
      <div class="relative bg-black" style="aspect-ratio: 16/9;">
        <!-- Main Video (Remote) -->
        <div class="absolute inset-0 flex items-center justify-center">
          <div class="text-center text-white">
            <IconifyIcon icon="heroicons:user-circle-20-solid" class="w-24 h-24 mx-auto mb-3 opacity-50" />
            <p class="text-lg font-medium">Remote User</p>
            <p class="text-sm opacity-70">Video would appear here</p>
          </div>
        </div>

        <!-- Self Video (Picture-in-Picture) -->
        <div class="absolute bottom-4 right-4 w-32 h-24 bg-gray-800 rounded-lg flex items-center justify-center border-2 border-white">
          <div class="text-center text-white text-xs">
            <IconifyIcon icon="heroicons:video-camera-20-solid" class="w-8 h-8 mx-auto mb-1 opacity-50" />
            <p>You</p>
          </div>
        </div>

        <!-- Call Controls Overlay -->
        <div class="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex items-center gap-3 bg-black bg-opacity-50 rounded-full px-4 py-2">
          <button
            @click="toggleMute"
            :class="[
              'w-10 h-10 rounded-full flex items-center justify-center transition-colors',
              isMuted ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-600 hover:bg-gray-700'
            ]"
          >
            <IconifyIcon :icon="isMuted ? 'heroicons:microphone-slash-20-solid' : 'heroicons:microphone-20-solid'" class="w-5 h-5 text-white" />
          </button>

          <button
            @click="toggleVideo"
            :class="[
              'w-10 h-10 rounded-full flex items-center justify-center transition-colors',
              isVideoOff ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-600 hover:bg-gray-700'
            ]"
          >
            <IconifyIcon :icon="isVideoOff ? 'heroicons:video-camera-slash-20-solid' : 'heroicons:video-camera-20-solid'" class="w-5 h-5 text-white" />
          </button>

          <button
            @click="toggleScreenShare"
            :class="[
              'w-10 h-10 rounded-full flex items-center justify-center transition-colors',
              isScreenSharing ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-600 hover:bg-gray-700'
            ]"
          >
            <IconifyIcon icon="heroicons:computer-desktop-20-solid" class="w-5 h-5 text-white" />
          </button>

          <button
            @click="endCall"
            class="w-10 h-10 rounded-full bg-red-600 hover:bg-red-700 flex items-center justify-center transition-colors"
          >
            <IconifyIcon icon="heroicons:phone-x-mark-20-solid" class="w-5 h-5 text-white" />
          </button>
        </div>

        <!-- Connection Quality Indicator -->
        <div class="absolute top-4 left-4 flex items-center gap-2 bg-black bg-opacity-50 rounded-full px-3 py-1">
          <div class="flex items-center gap-1">
            <div class="w-2 h-2 bg-green-400 rounded-full"></div>
            <span class="text-xs text-white">HD</span>
          </div>
          <span class="text-xs text-white">•</span>
          <div class="flex items-center gap-1">
            <IconifyIcon icon="heroicons:wifi-20-solid" class="w-3 h-3 text-green-400" />
            <span class="text-xs text-white">Strong</span>
          </div>
        </div>
      </div>

      <!-- Call Info -->
      <div class="p-4 border-t border-gray-200">
        <div class="grid grid-cols-3 gap-4 text-center">
          <div>
            <p class="text-xs text-gray-500">Quality</p>
            <p class="text-sm font-medium text-gray-900">1080p HD</p>
          </div>
          <div>
            <p class="text-xs text-gray-500">Latency</p>
            <p class="text-sm font-medium text-green-600">{{ latency }}ms</p>
          </div>
          <div>
            <p class="text-xs text-gray-500">Bandwidth</p>
            <p class="text-sm font-medium text-blue-600">{{ bandwidth }}Mbps</p>
          </div>
        </div>

        <!-- Feature Toggles -->
        <div class="mt-4 flex items-center justify-center gap-4">
          <label class="flex items-center gap-2 text-sm">
            <input type="checkbox" v-model="autoGainControl" class="rounded" />
            <span class="text-gray-700">Auto Gain</span>
          </label>
          <label class="flex items-center gap-2 text-sm">
            <input type="checkbox" v-model="noiseSuppression" class="rounded" />
            <span class="text-gray-700">Noise Reduction</span>
          </label>
          <label class="flex items-center gap-2 text-sm">
            <input type="checkbox" v-model="echoCancellation" class="rounded" />
            <span class="text-gray-700">Echo Cancel</span>
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import IconifyIcon from '../../IconifyIcon.vue'

// Reactive state
const isMuted = ref(false)
const isVideoOff = ref(false)
const isScreenSharing = ref(false)
const isInCall = ref(false)
const callDuration = ref('0:00')
const latency = ref(42)
const bandwidth = ref(2.8)
const autoGainControl = ref(true)
const noiseSuppression = ref(true)
const echoCancellation = ref(true)

let callTimer: number | null = null
let metricsTimer: number | null = null

// Methods
const toggleMute = () => {
  isMuted.value = !isMuted.value
}

const toggleVideo = () => {
  isVideoOff.value = !isVideoOff.value
}

const toggleScreenShare = () => {
  isScreenSharing.value = !isScreenSharing.value
}

const endCall = () => {
  isInCall.value = false
  if (callTimer) {
    clearInterval(callTimer)
    callTimer = null
  }
  if (metricsTimer) {
    clearInterval(metricsTimer)
    metricsTimer = null
  }
  callDuration.value = '0:00'
}

const startCall = () => {
  isInCall.value = true
  let seconds = 0

  callTimer = window.setInterval(() => {
    seconds++
    const minutes = Math.floor(seconds / 60)
    const secs = seconds % 60
    callDuration.value = `${minutes}:${secs.toString().padStart(2, '0')}`
  }, 1000)
}

const updateMetrics = () => {
  // Simulate realistic metric variations
  latency.value = Math.max(20, Math.min(150, latency.value + (Math.random() - 0.5) * 10))
  bandwidth.value = Math.max(1, Math.min(5, bandwidth.value + (Math.random() - 0.5) * 0.5))
}

// Lifecycle
onMounted(() => {
  startCall()
  metricsTimer = window.setInterval(updateMetrics, 2000)
})

onUnmounted(() => {
  endCall()
})
</script>

<style scoped>
.video-demo {
  /* Component-specific styles */
}
</style>